import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./userMenu.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { TBID_URL } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { generateBaseUrl } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { i18nStore, selectI18nModule } from '~/modules/stores/i18nStore';
import { userStore } from '~/modules/stores/userStore';
import { ENGLISH_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
class UserMenu extends LightningElement {
  constructor(...args) {
    super(...args);
    this.DEFAULT_BACKGROUND_IMAGE = '/images/arid-dunes.png';
    this.i18n = void 0;
    this.user = void 0;
    this._activeItem = 0;
    this._menuClassname = 'dropdown-menu';
    this._backDropClassName = 'backdrop';
    this._shouldFocusActiveItem = false;
    this._shouldFocusDropdown = false;
    this.expandedMenu = false;
  }
  get menuItems() {
    let itemCounter = 1;
    const menuItems = [{
      id: 'profile',
      text: this.i18n.header.profile(),
      link: `${TBID_URL}/id`,
      active: itemCounter++ === this._activeItem
    }, {
      id: 'settings',
      text: this.i18n.header.settings(),
      link: `${TBID_URL}/settings`,
      active: itemCounter++ === this._activeItem
    }];
    if (FeatureFlags.FF_CUSTOMER_CONSOLE && getUserLanguage() === ENGLISH_LANGUAGE_CODE) {
      menuItems.push({
        id: 'orders',
        text: this.i18n.header.installAndPurchase(),
        link: '/' + Routes.ORDERS,
        active: itemCounter++ === this._activeItem
      });
    }
    // only certain users can see the My Installs and Subscriptions link
    if (!FeatureFlags.FF_CUSTOMER_CONSOLE && getUserLanguage() === ENGLISH_LANGUAGE_CODE && this.user?.canCheckout && this.user?.canInstall) {
      menuItems.push({
        id: 'myInstalls',
        text: this.i18n.header.myInstalls(),
        link: `${generateBaseUrl()}/appxMyAccount`,
        active: itemCounter++ === this._activeItem
      });
    }
    menuItems.push({
      id: 'logout',
      text: this.i18n.header.logout(),
      // eslint-disable-next-line no-script-url
      link: 'javascript:SFIDWidget.logout();',
      active: itemCounter++ === this._activeItem
    });
    return menuItems;
  }
  get backgroundImage() {
    return this.user?.backgroundImageUrl || this.DEFAULT_BACKGROUND_IMAGE;
  }
  get showUser() {
    return this.user?.tbidProfile?.active;
  }
  get profileName() {
    const tbidProfile = this.user?.tbidProfile;
    return this.i18n.shared.userName({
      FIRST_NAME: tbidProfile?.given_name ?? '',
      LAST_NAME: tbidProfile?.family_name ?? ''
    });
  }
  get profileImage() {
    return this.user?.tbidProfile?.picture ?? '';
  }
  get menuClassName() {
    return this._menuClassname + (this.expandedMenu ? ' expanded' : '');
  }
  get backDropClassName() {
    return this._backDropClassName + (this.expandedMenu ? '' : ' hidden');
  }

  // @ts-expect-error * Automated Type Error Fix
  handleAnalytics(event) {
    const menuIdentifier = 'user menu';
    const linkClicked = event.target.querySelector('a');
    const clickText = linkClicked?.textContent?.toLowerCase() || menuIdentifier;
    event.detail.navLink = {
      menuItem: menuIdentifier,
      clickText: clickText,
      clickUrl: linkClicked?.href,
      destinationType: 'internal',
      ctaClick: false,
      navLevel: linkClicked ? 'l2' : 'l1'
    };
    event.detail.elementType = 'loginNavLink';
  }
  handleTriggerClick() {
    this.toogleMenu();
    this._shouldFocusDropdown = true;
  }
  toogleMenu() {
    this.expandedMenu = !this.expandedMenu;
    if (!this.expandedMenu) {
      this._activeItem = 0;
      this._shouldFocusActiveItem = false;
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleTriggerKeyDown(event) {
    if (event.key === 'ArrowDown' || event.key === 'Enter') {
      this._activeItem = 1;
      this._shouldFocusActiveItem = true;
      this.toogleMenu();
      event.preventDefault();
    }
  }
  handleMenuKeyDown(event) {
    switch (event.key) {
      case 'Tab':
        if (event.shiftKey) {
          this.selectPreviousMenuItem();
        } else {
          this.selectNextMenuItem();
        }
        event.preventDefault();
        break;
      case 'Escape':
        this.toogleMenu();
        this.focusTrigger();
        break;
      case 'ArrowDown':
        this.selectNextMenuItem();
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.selectPreviousMenuItem();
        event.preventDefault();
        break;
      case 'Enter':
        this.activateMenuItem();
        break;
      default:
        event.preventDefault();
    }
  }
  renderedCallback() {
    if (this._shouldFocusActiveItem) {
      this.focusActiveItem();
    }
    if (this._shouldFocusDropdown) {
      this.focusDropdown();
      this._shouldFocusDropdown = false;
    }
  }
  focusActiveItem() {
    this.template.querySelector('a[data-active="true"]').focus();
  }
  focusDropdown() {
    this.template.querySelector('div[data-role="dropdown"]').focus();
  }
  focusTrigger() {
    this.template.querySelector('button[data-role="trigger"]').focus();
  }
  selectNextMenuItem() {
    this._activeItem++;
    if (this._activeItem > this.menuItems.length) {
      this._activeItem = 1;
    }
  }
  selectPreviousMenuItem() {
    this._activeItem--;
    if (this._activeItem < 1) {
      this._activeItem = this.menuItems.length;
    }
  }
  activateMenuItem() {
    if (this._activeItem > 0) {
      this.template.querySelector('a[data-active="true"]').click();
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(UserMenu, {
  wire: {
    i18n: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: i18nStore,
          selector: selectI18nModule
        };
      }
    },
    user: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: userStore,
          selector: state => ({
            canInstall: state.canInstall,
            canCheckout: state.canCheckout,
            tbidProfile: state.tbidProfile,
            backgroundImageUrl: state.backgroundImageUrl
          }),
          equalityFn
        };
      }
    }
  },
  fields: ["DEFAULT_BACKGROUND_IMAGE", "_activeItem", "_menuClassname", "_backDropClassName", "_shouldFocusActiveItem", "_shouldFocusDropdown", "expandedMenu"]
});
const __lwc_component_class_internal = _registerComponent(UserMenu, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;